import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dropdown } from "react-bootstrap";
import { toast } from "sonner";
import Swal from "sweetalert2";
import { privateApi } from "../../../api/api";
import { useCallback } from "react";
import RequireAuth from "../../../auth/middleware/RequireAuth";
import ReadOnlyGuard from "../../../shared/components/ReadOnlyGuard";

interface LinkRatesProps {
    rate: any;
    onUpdateLinkRate: (rate_id: string) => void;
}

const LinkRates: React.FC<LinkRatesProps> = ({ rate, onUpdateLinkRate }) => {
    const queryClient = useQueryClient();

    const { mutate } = useMutation({
        mutationFn: (rate_id: string) => privateApi.delete(`/v4/rate-plan/${rate_id}`),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                predicate: (query) => query.queryKey.some((key) => ['rooms', 'rates'].includes(key as string))
            })

            toast.success('Link Rate deleted successfully', {
                id: 'delete-link-rate'
            });

            Swal.fire({
                title: 'Reminder',
                html: 'Link rate deleted. <strong>Make sure to re-map it in all channels</strong> so the OTA mapping doesn\'t get left behind.',
                icon: 'info',
                confirmButtonText: 'Got it',
            });
        },
        onError: () => {
            toast.error('Failed to delete link rate', {
                id: 'delete-link-rate'
            });
        },
    });

    const handleDelete = useCallback((rate_id: string) => {
        Swal.fire({
            title: 'Are you sure?',
            html: 'You will not be able to recover this link rate!<br /><br /><strong>Check the Channex mapping</strong> for this link rate before deleting — it may still be linked to OTA channels.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                toast.loading('Deleting link rate...', {
                    id: 'delete-link-rate'
                });
                mutate(rate_id);
            }
        });
    }, [mutate]);

    return (
        <>
            {
                rate.map((child: any) => (
                    <tr key={child.id} className="child-rate">
                        <td></td>
                        <td style={{ paddingLeft: '90px' }}>
                            {child.name} (Max Guests: {child.guest_max})
                        </td>
                        <td>—</td>
                        <td>—</td>
                        <td className="text-end">
                            <Dropdown>
                                <Dropdown.Toggle variant="default" id="action-dropdown">
                                    Actions
                                </Dropdown.Toggle>
                                <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_update']} asComponent>
                                        <ReadOnlyGuard mode="hide">
                                            <Dropdown.Item onClick={() => onUpdateLinkRate(child.id)}>Update</Dropdown.Item>
                                        </ReadOnlyGuard>
                                    </RequireAuth>
                                    {/* Remove stays enabled while the property is inactive — deliberate carve-out, see IndexAi/property-management.md */}
                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_delete']} asComponent>
                                        <Dropdown.Item className="text-danger" onClick={() => handleDelete(child.id)}>Remove</Dropdown.Item>
                                    </RequireAuth>
                                </Dropdown.Menu>
                            </Dropdown>
                        </td>
                    </tr>
                ))
            }
        </>
    );
};

export default LinkRates;